import { useState } from "react";
import { Upload, X, Loader2 } from "lucide-react";
import { api, imgUrl } from "../lib/api";

/**
 * ImageUploader: file input with preview that uploads to /api/upload and returns the URL.
 * Props:
 *  - value: current URL string
 *  - onChange: (url) => void
 *  - testId: data-testid prefix
 */
export default function ImageUploader({ value, onChange, testId = "image-uploader" }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError("");
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await api.post("/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(res.data.url);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || "Échec de l'upload");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div data-testid={testId} className="space-y-2">
      <div className="flex items-start gap-3">
        {value ? (
          <div className="relative">
            <img
              src={imgUrl(value)}
              alt=""
              className="w-24 h-24 object-cover rounded-sm bg-stone-100 border border-stone-200"
              onError={(e) => (e.currentTarget.style.opacity = 0.3)}
            />
            <button
              type="button"
              data-testid={`${testId}-clear`}
              onClick={() => onChange("")}
              className="absolute -top-2 -right-2 bg-white border border-stone-300 rounded-full p-0.5 hover:bg-red-50 hover:border-red-300"
            >
              <X size={14} className="text-stone-700" />
            </button>
          </div>
        ) : (
          <div className="w-24 h-24 border-2 border-dashed border-stone-300 rounded-sm flex items-center justify-center text-stone-400 bg-stone-50">
            <Upload size={20} />
          </div>
        )}
        <div className="flex-1 space-y-2">
          <label
            data-testid={`${testId}-button`}
            className="inline-flex items-center gap-2 px-3 py-2 bg-stone-900 hover:bg-stone-800 text-white text-sm rounded-sm cursor-pointer"
          >
            {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
            {uploading ? "Envoi…" : value ? "Remplacer" : "Téléverser"}
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              onChange={handleFile}
              disabled={uploading}
              className="hidden"
              data-testid={`${testId}-input`}
            />
          </label>
          <input
            type="url"
            placeholder="…ou collez une URL d'image"
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            className="block w-full px-3 py-2 bg-white border border-stone-300 rounded-sm text-sm focus:ring-2 focus:ring-orange-800 focus:outline-none"
            data-testid={`${testId}-url`}
          />
          {error && <p className="text-xs text-red-700">{String(error)}</p>}
          <p className="text-xs text-stone-500">JPG / PNG / WEBP — max 5 Mo</p>
        </div>
      </div>
    </div>
  );
}
